import React, { useRef } from 'react';
import columnResizeHelper from '../../Helpers/columnResizeHelper';

// The drag grip at the right edge of a resizable column's header cell.
//
// The drag moves a guide line, not the column: while the pointer is down nothing in the table
// changes, and the new width is applied once on release. That is the only way a wide grid can
// track the cursor at frame rate - a column's width belongs to the whole column, so the header
// cannot be resized on its own, and re-laying the table out per mouse move means touching every
// cell in every row (the unpaged report is ~1,500 rows x ~50 columns, tens of thousands of
// cells). Freezing the table to `table-layout: fixed` for the drag was tried first and was still
// a frame or two behind: fixed layout skips the intrinsic measuring, but the browser must still
// lay out and paint every one of those cells. It is also the gesture people know from Excel.
//
// The width itself is written straight onto the table's CSS custom property; onResize only
// records the number (cookie + the grid's own copy) and deliberately triggers no re-render,
// which would re-render every row to reproduce what the browser has just painted.
export default function ColumnResizeHandle(props) {
  const drag = useRef(null);

  // Two elements for the duration of the drag, both appended to the body so neither the header
  // cell's `overflow: hidden` nor the grid's own scrolling can clip them: the guide line, drawn
  // at the column's prospective right edge, and a full-viewport shield that carries the resize
  // cursor and swallows hover. The shield is why nothing is flagged on the table itself —
  // `cursor` and `user-select` inherit, so a class on the table that sets either one restyles
  // every cell in the grid, which is a visible freeze at both ends of the drag.
  const createShield = () => {
    const shield = document.createElement('div');
    shield.className = 'col-resize-shield';
    document.body.appendChild(shield);
    return shield;
  };

  const createGuide = (bounds) => {
    const guide = document.createElement('div');
    guide.className = 'col-resize-guide';
    guide.style.top = `${bounds.top}px`;
    guide.style.height = `${bounds.height}px`;
    document.body.appendChild(guide);
    return guide;
  };

  // The "the table is catching up" bar, drawn along the top edge of the grid for the length of
  // the layout pass. Its motion is a `transform` animation on the inner block, which the
  // compositor keeps running while the main thread is blocked — the same reason the guide pulses
  // on `opacity`. Anything driven from the main thread (a spinner, a colour transition) would
  // simply stand still for the duration.
  const createProgressBar = (bounds) => {
    const bar = document.createElement('div');
    bar.className = 'col-resize-progress';
    bar.style.left = `${bounds.left}px`;
    bar.style.top = `${bounds.top}px`;
    bar.style.width = `${bounds.width}px`;
    bar.appendChild(document.createElement('i'));
    document.body.appendChild(bar);
    return bar;
  };

  const removeElement = (element) => {
    if (element && element.parentNode) element.parentNode.removeChild(element);
  };

  const moveGuide = () => {
    const state = drag.current;
    if (!state) return;
    state.frame = 0;
    const width = columnResizeHelper.clampWidth(state.startWidth + (state.pointerX - state.startX));
    if (width === state.width) return;
    state.width = width;
    state.moved = true;
    state.guide.style.left = `${state.startEdge + width - state.startWidth}px`;
  };

  // Changing a column's width is one layout pass over every cell in the grid, and on the report
  // that is long enough to see. It cannot be animated away — it blocks the main thread — so it
  // is staged instead: the progress bar (and, on a drag, the guide line's pulse) go up, TWO
  // animation frames pass so that state is actually painted, and only then is the width written.
  // Both animations are composited, so they keep running while the main thread is busy and the
  // pause reads as the table catching up rather than a freeze.
  //
  // Every path that changes a width goes through here — the drag release and the double-click
  // reset alike. The reset looks instant to write but costs exactly the same relayout, so
  // leaving it bare made a double-click feel like it had done nothing at all.
  const commitWidth = (context, writeWidth) => {
    const bar = createProgressBar(context.bounds);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        writeWidth();
        // Which cells the new width cuts can only be known once it has been applied, and the
        // relayout for it has just been paid, so the measuring pass rides along with it.
        columnResizeHelper.markClippedCells(context.table, props.columnIndex);
        removeElement(context.guide);
        removeElement(context.shield);
        removeElement(bar);
        flashHeaderCell(context.headerCell);
      })
    );
  };

  // A short tint on the heading once the column has moved, so the eye lands on what changed
  // after the pause. One cell, so it costs nothing.
  const flashHeaderCell = (headerCell) => {
    headerCell.classList.remove('settling');
    headerCell.addEventListener('animationend', () => headerCell.classList.remove('settling'), { once: true });
    requestAnimationFrame(() => headerCell.classList.add('settling'));
  };

  const endDrag = () => {
    const state = drag.current;
    if (!state) return;
    drag.current = null;
    if (state.frame) cancelAnimationFrame(state.frame);
    state.grip.classList.remove('dragging');
    // A press that never moved is a click on the grip, not a resize: leave the column alone
    // rather than pinning it at the width it happened to have.
    if (!state.moved) {
      removeElement(state.guide);
      removeElement(state.shield);
      return;
    }
    state.guide.classList.add('applying');
    commitWidth(state, () => {
      columnResizeHelper.setTableColumnWidth(state.table, props.columnIndex, state.width);
      props.onResize(props.columnKey, state.width);
    });
  };

  const startDrag = (event) => {
    // The header cell itself is the sort button, so the grip has to keep the drag to itself.
    event.preventDefault();
    event.stopPropagation();
    const grip = event.currentTarget;
    const headerCell = grip.closest('th');
    const table = grip.closest('table');
    const scroller = grip.closest('.table-wrap');
    if (!headerCell || !table || !scroller) return;
    // The drag starts from the cell's content width, not its border box: the custom property
    // sizes the block wrapper inside the cell, so starting from the border box would widen the
    // column by its own padding the moment it is first dragged.
    const cellRect = headerCell.getBoundingClientRect();
    const cellStyle = window.getComputedStyle(headerCell);
    const startWidth = cellRect.width - parseFloat(cellStyle.paddingLeft) - parseFloat(cellStyle.paddingRight);
    const bounds = scroller.getBoundingClientRect();
    const shield = createShield();
    const guide = createGuide(bounds);
    drag.current = {
      table,
      grip,
      headerCell,
      bounds,
      shield,
      guide,
      startX: event.clientX,
      pointerX: event.clientX,
      startEdge: cellRect.right,
      startWidth,
      width: startWidth,
      moved: false,
      frame: 0,
    };
    guide.style.left = `${cellRect.right}px`;
    grip.classList.add('dragging');
    grip.setPointerCapture(event.pointerId);
  };

  const moveDrag = (event) => {
    const state = drag.current;
    if (!state) return;
    state.pointerX = event.clientX;
    if (!state.frame) state.frame = requestAnimationFrame(moveGuide);
  };

  // Back to the column's natural width. Staged like the drag release, minus the guide line:
  // there is no edge to aim at here, but the relayout is the same one, so the grid gets the same
  // progress bar and the heading the same tint at the end.
  const resetWidth = (event) => {
    event.stopPropagation();
    const grip = event.currentTarget;
    const headerCell = grip.closest('th');
    const table = grip.closest('table');
    const scroller = grip.closest('.table-wrap');
    if (!headerCell || !table || !scroller) return;
    const context = {
      table,
      headerCell,
      bounds: scroller.getBoundingClientRect(),
      shield: createShield(),
      guide: null,
    };
    commitWidth(context, () => {
      columnResizeHelper.setTableColumnWidth(table, props.columnIndex, null);
      props.onResize(props.columnKey, null);
    });
  };

  return (
    <span
      className='col-grip'
      role='separator'
      aria-orientation='vertical'
      title='Drag to resize this column, double-click to reset it'
      onPointerDown={startDrag}
      onPointerMove={moveDrag}
      onPointerUp={endDrag}
      onLostPointerCapture={endDrag}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={resetWidth}
    />
  );
}
